import { useState } from "react";
import { Store } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";

export default function StoreSwitcher({ testidPrefix = "" }) {
  const { stores, active, multiTenant, switchStore, cartCount } = useStore();
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!multiTenant || stores.length < 2) return null;

  const doSwitch = async (id) => {
    setBusy(true);
    try {
      await switchStore(id);
      toast.success(`Switched to ${stores.find((s) => s.store_id === id)?.store_name || id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to switch store");
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  const onChange = (id) => {
    if (!id || id === active?.store_id) return;
    if (cartCount > 0) setPending(id); else doSwitch(id);
  };

  const target = stores.find((s) => s.store_id === pending);

  return (
    <>
      <Select value={active?.store_id || ""} onValueChange={onChange} disabled={busy}>
        <SelectTrigger className="h-9 w-full md:w-[220px] bg-background border border-emerald-900/20" data-testid={`${testidPrefix}store-switcher-trigger`}>
          <div className="flex items-center gap-2 overflow-hidden">
            <Store className="h-4 w-4 shrink-0 text-emerald-700" />
            <SelectValue>
              <span className="truncate">{active ? `${active.tenant_name} | ${active.store_name}` : "Select store"}</span>
            </SelectValue>
          </div>
        </SelectTrigger>
        <SelectContent>
          {stores.map((s) => (
            <SelectItem key={s.store_id} value={s.store_id} data-testid={`${testidPrefix}store-option-${s.store_slug}`}>
              {s.tenant_name} | {s.store_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent data-testid="store-switch-cart-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard the open cart?</AlertDialogTitle>
            <AlertDialogDescription>
              The current {active?.store_name} cart has {cartCount} item(s) that have not been sold.
              Switching to {target?.store_name} will clear it — products and prices never carry across stores.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="store-switch-cancel">Stay in {active?.store_name}</AlertDialogCancel>
            <AlertDialogAction data-testid="store-switch-confirm" onClick={() => doSwitch(pending)}>
              Clear cart & switch
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
